import { useRef, useState } from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { type Swiper as SwiperRef } from 'swiper';
import { Box, Chip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { MediaSlider as IMediaSlider, Video, MediaSliderContentsItem } from '@/types/generated';
import { Maybe } from '@/types';
import AspectRatioPaddingBox from '@/components/AspectRatioPaddingBox';
import FocalPointImage from '@/components/FocalPointImage/FocalPointImage';
import VideoContent from '@/components/VideoContent/VideoContent';
import TAGSvgIcon from '@/components/TAGSvgIcon';

import {
	badgeStyles,
	manualRecordIconStyles,
	manualRecordIconWrapper,
	swiperActionsWrapper,
	getWrapperStyles,
	getSwiperStyles,
	buttonStyles,
} from './MediaSlider.styles';

interface IProps extends Partial<IMediaSlider> {
	fullHeight?: boolean;
	enableSliderControlsBackgroundGradient?: boolean;
}

export default function MediaSlider({
	contentsCollection,
	badge,
	fullHeight,
	enableSliderControlsBackgroundGradient,
}: IProps) {
	const swiperRef = useRef<SwiperRef>();
	const [activeStep, setActiveStep] = useState(0);
	const theme = useTheme();
	const isSmall: boolean = useMediaQuery(theme.breakpoints.down('md'));

	const mediaContents = contentsCollection?.items;

	const handleStepChange = (step: number) => {
		setActiveStep(step);
	};

	if (!mediaContents?.length) {
		return null;
	}

	return (
		<Box data-test-id="media_slider" sx={getWrapperStyles(fullHeight)}>
			{badge && (
				<Chip
					label={badge.label}
					variant="heavyDefault" // to do: export type from theme package and then change to badge.variant
					sx={badgeStyles}
					data-test-id="media_slider_badge"
				/>
			)}
			<Swiper
				onSlideChange={(swiper) => handleStepChange(swiper.activeIndex)}
				onSwiper={(swiper) => {
					swiperRef.current = swiper;
				}}
				style={getSwiperStyles(fullHeight)}
			>
				{mediaContents.map((media: Maybe<MediaSliderContentsItem>, index: number) => {
					if (media) {
						return (
							<SwiperSlide key={`swiper_media_${index}`}>
								<AspectRatioPaddingBox aspectRatio="1:1">
									{media.__typename === 'FocalPointImage' ? (
										<FocalPointImage {...media} dataTestId={`media_focal_image_${media.sys.id}`} />
									) : (
										<VideoContent
											dataTestId={`media_video_${media.sys.id}`}
											controllableVideo={media as Video}
											videoControlsSx={{ bottom: '3.85rem' }}
										/>
									)}
								</AspectRatioPaddingBox>
							</SwiperSlide>
						);
					}
					return null;
				})}
			</Swiper>
			{mediaContents.length > 1 ? (
				<Box
					sx={swiperActionsWrapper(enableSliderControlsBackgroundGradient)}
					data-test-id="media_slider_actions"
				>
					{isSmall ? null : (
						<IconButton
							onClick={() => swiperRef.current?.slidePrev()}
							sx={buttonStyles(theme)}
							disabled={activeStep === 0}
							data-test-id="media_slider_left_chevron_button"
						>
							<TAGSvgIcon icon="ChevronLeftFunc" size={40} />
						</IconButton>
					)}

					<Box sx={manualRecordIconWrapper}>
						{mediaContents.map((_, index) => (
							<FiberManualRecordIcon
								key={`manual_record_icon_${index}`}
								data-test-id={`media_slider_manual_record_icon_${index}`}
								onClick={() => swiperRef.current?.slideTo(index)}
								sx={manualRecordIconStyles(index, activeStep, theme)}
							/>
						))}
					</Box>

					{isSmall ? null : (
						<IconButton
							onClick={() => swiperRef.current?.slideNext()}
							sx={buttonStyles(theme)}
							disabled={activeStep === mediaContents.length - 1}
							data-test-id="media_slider_right_chevron_button"
						>
							<TAGSvgIcon icon="ChevronRightFunc" size={40} />
						</IconButton>
					)}
				</Box>
			) : null}
		</Box>
	);
}
